import type { UserType } from "@shared/types";
import { ArrowLeft, Loader2, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { StudioButton } from "@/features/studio/theme";
import { useRoleActions } from "../hooks/use-role-actions";
import { useUserRoles } from "../hooks/use-user-roles";

export interface OnboardingExit {
	/**
	 * Another role the account holds, to go back to. Null once this role's
	 * profile is created, when the page shows its success step.
	 */
	backTo: UserType | null;
	/**
	 * The flow's own Back from its first step. Offered only once the role list
	 * has loaded and shows no other role: the server refuses the role picker's
	 * change once any of the account's roles is set up, so the picker is no way
	 * out for an account holding another role, nor for one whose role list
	 * could not be read.
	 */
	onExit: (() => void) | undefined;
	/**
	 * Fetch the role list again. Set only while the list could not be read
	 * and this role's profile is not yet created.
	 */
	retry: (() => void) | undefined;
	/** A fetch of the role list is in flight. */
	retrying: boolean;
}

/**
 * How an onboarding page lets the user leave. An account that holds another
 * role (one added a role and is setting it up) goes back to that role; any
 * other account keeps `pickerExit`, the way back to the role picker.
 */
export function useOnboardingExit(pickerExit: () => void): OnboardingExit {
	const { data, isError, isFetching, refetch } = useUserRoles();
	const { user } = useAuthStore();
	const profileCreated = user?.profileCompleted === true;
	const otherRole = data?.roles.find((held) => !held.isActive)?.role ?? null;
	const backTo = profileCreated ? null : otherRole;
	return {
		backTo,
		onExit: data && !otherRole ? pickerExit : undefined,
		retry:
			isError && !data && !profileCreated ? () => void refetch() : undefined,
		retrying: isFetching,
	};
}

/**
 * The control above an onboarding flow: "Back to <role>" when the account
 * holds another role, or a retry when the role list could not be read.
 */
export function OnboardingExitControl({
	exit,
}: {
	exit: OnboardingExit;
}): React.JSX.Element | null {
	if (exit.backTo) return <BackToRoleButton role={exit.backTo} />;
	if (exit.retry) {
		return <RolesLoadFailed onRetry={exit.retry} retrying={exit.retrying} />;
	}
	return null;
}

/**
 * Only the retry for a role list that could not be read, for a flow that
 * places "Back to <role>" itself (`BackToRoleButton` with `variant="panel"`).
 */
export function RolesLoadRetry({
	exit,
}: {
	exit: OnboardingExit;
}): React.JSX.Element | null {
	if (!exit.retry) return null;
	return <RolesLoadFailed onRetry={exit.retry} retrying={exit.retrying} />;
}

function RolesLoadFailed({
	onRetry,
	retrying,
}: {
	onRetry: () => void;
	retrying: boolean;
}): React.JSX.Element {
	const { t } = useTranslation();
	return (
		<div
			role="alert"
			className="mb-6 flex min-h-11 items-center gap-3 font-body text-sm text-dashboard-muted"
		>
			<span>{t("roleSwitcher.loadFailed")}</span>
			<button
				type="button"
				disabled={retrying}
				onClick={onRetry}
				className="flex min-h-11 items-center gap-2 font-medium text-dashboard-text transition-colors duration-200 hover:text-dashboard-accent disabled:opacity-60"
			>
				{retrying ? (
					<Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
				) : (
					<RotateCw aria-hidden="true" className="h-4 w-4" />
				)}
				{t("roleSwitcher.retry")}
			</button>
		</div>
	);
}

/**
 * "Back to <role>": switches to `role` and goes to its landing page. `link`
 * (the default) sits above a flow; `panel` is a secondary button for a flow
 * whose later steps put their Back beside Continue at the bottom of the
 * panel, so the first step's way back sits in the same place.
 */
export function BackToRoleButton({
	role,
	variant = "link",
}: {
	role: UserType;
	variant?: "link" | "panel";
}): React.JSX.Element {
	const { t } = useTranslation();
	const { pendingRole, switchTo } = useRoleActions();
	const label = t("roleSwitcher.backTo", {
		role: t(`roleSession.roleNames.${role}`),
	});
	if (variant === "panel") {
		return (
			<StudioButton
				type="button"
				variant="secondary"
				disabled={pendingRole !== null}
				onClick={() => void switchTo(role)}
				// Its own width, on one line; Continue takes the rest.
				style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
			>
				{pendingRole ? (
					<Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
				) : null}
				{label}
			</StudioButton>
		);
	}
	return (
		<button
			type="button"
			disabled={pendingRole !== null}
			onClick={() => void switchTo(role)}
			className="mb-6 flex min-h-11 items-center gap-2 font-body text-sm text-dashboard-muted transition-colors duration-200 hover:text-dashboard-text disabled:opacity-60"
		>
			{pendingRole ? (
				<Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
			) : (
				<ArrowLeft aria-hidden="true" className="h-4 w-4" />
			)}
			{label}
		</button>
	);
}
